import React, { useState, useCallback, useRef, useEffect } from 'react';
import api from '../lib/api';
import { trackEvent } from '../lib/firebase';
import { fbTrack } from '../lib/fbpixel';
import { getStoredPromo } from '../lib/promo';
import { useAuth } from '../contexts/AuthContext';
import LoginRequiredDialog from '../components/auth/LoginRequiredDialog';

// Persists gated-checkout args across a full-page Google OAuth redirect so the
// user lands back on the payment page and we auto-resume the Razorpay modal.
const PENDING_KEY = 'today_pending_checkout';
const PENDING_TTL_MS = 10 * 60 * 1000; // 10 min

export default function useRazorpay() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const pendingArgsRef = useRef(null);
  const { user, isGuest, deviceId } = useAuth();

  const _startCheckout = useCallback(async (args) => {
    const { deviceId, itemType, itemId, amount, currency, userName, userEmail, userContact, onSuccess, onFailure } = args;
    setLoading(true);
    setError('');

    try {
      // Create order with correct per-item pricing in the user's currency.
      // If a promo code was captured from the URL earlier, silently include it —
      // the backend is the sole source of truth for valid codes & percent-off.
      const { data: order } = await api.post('/payment/create-order', {
        device_id: deviceId,
        item_type: itemType || 'premium',
        item_id: itemId || null,
        amount: amount || 2999,
        currency: currency || 'INR',
        coupon_code: getStoredPromo() || null,
        user_email: userEmail || null,
        user_phone: userContact || null,
      });

      if (!order.order_id) {
        throw new Error('Failed to create payment order');
      }

      const displayAmount = order.amount_display
        ? `${order.symbol || ''}${order.amount_display}`
        : `₹${amount}`;

      const options = {
        key: order.key_id,
        amount: order.amount,                      // smallest unit (paise/cents/yen)
        currency: order.currency || 'INR',
        name: 'Today: Daily Reset',
        description: `${order.feature_name || 'Purchase'} · ${displayAmount}`,
        order_id: order.order_id,
        handler: async (response) => {
          try {
            const { data: verification } = await api.post('/payment/verify', {
              device_id: deviceId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setLoading(false);
            // GA4 recommended `purchase` event shape — includes `items` array for e-commerce reports.
            const feature_name =
              itemType === 'premium' ? 'Today Premium' :
              itemType === 'personality_report' ? 'EQ Personality Report' :
              itemType === 'iq_report' ? 'IQ Cognitive Report' :
              itemType === 'program' ? `Program: ${itemId}` : (itemType || 'Purchase');
            const value = Number(order.amount_display || amount || 0);
            const valueInr = Number(amount || 0);
            trackEvent('purchase', {
              transaction_id: response.razorpay_payment_id,
              value,
              currency: order.currency || 'INR',
              value_inr: valueInr,
              item_type: itemType || 'premium',
              item_id: itemId || null,
              items: [{
                item_id: itemId || itemType || 'premium',
                item_name: feature_name,
                item_category: itemType || 'premium',
                price: value,
                quantity: 1,
              }],
            });
            // Meta Pixel — standard Purchase event (fuels Advantage+ bidding)
            fbTrack('Purchase', {
              value,
              currency: order.currency || 'INR',
              content_ids: [itemId || itemType || 'premium'],
              content_type: 'product',
              content_name: feature_name,
              content_category: itemType || 'premium',
            });
            if (onSuccess) onSuccess(verification);
          } catch (verifyErr) {
            setLoading(false);
            trackEvent('purchase_failed', {
              reason: 'verification_error',
              item_type: itemType || 'premium',
              item_id: itemId || null,
            });
            const msg = verifyErr.response?.data?.detail || 'Payment verification failed';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
            if (onFailure) onFailure(msg);
          }
        },
        prefill: {
          name: userName || '',
          email: userEmail || '',
          contact: userContact || '',
        },
        theme: {
          color: '#879B7D',
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setLoading(false);
        const msg = resp.error?.description || 'Payment failed';
        setError(msg);
        trackEvent('purchase_failed', {
          reason: resp.error?.reason || 'payment_failed',
          item_type: itemType || 'premium',
          item_id: itemId || null,
        });
        if (onFailure) onFailure(msg);
      });
      rzp.open();
      const _valueForBegin = Number(order.amount_display || amount || 0);
      const _featureName =
        itemType === 'premium' ? 'Today Premium' :
        itemType === 'personality_report' ? 'EQ Personality Report' :
        itemType === 'iq_report' ? 'IQ Cognitive Report' :
        itemType === 'program' ? `Program: ${itemId}` : (itemType || 'Purchase');
      trackEvent('begin_checkout', {
        item_type: itemType || 'premium',
        item_id: itemId || null,
        value: _valueForBegin,
        currency: order.currency || 'INR',
      });
      // Meta Pixel — InitiateCheckout (funnel prep for Purchase)
      fbTrack('InitiateCheckout', {
        value: _valueForBegin,
        currency: order.currency || 'INR',
        content_ids: [itemId || itemType || 'premium'],
        content_type: 'product',
        content_name: _featureName,
        content_category: itemType || 'premium',
      });
    } catch (err) {
      setLoading(false);
      const msg = err.response?.data?.detail || err.message || 'Payment error';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      if (onFailure) onFailure(msg);
    }
  }, []);

  // Public `pay()` — checks auth first unless the caller passes `allowGuest: true`.
  // If user is a guest and `allowGuest` is NOT set, opens the LoginRequiredDialog,
  // stashes the pay args, and resumes the purchase automatically after successful
  // authentication. Transactions & progress are then tied to a real account.
  const pay = useCallback(async (args) => {
    const isUnauthed = !user || isGuest || user?.guest;
    if (isUnauthed && !args.allowGuest) {
      pendingArgsRef.current = args;
      // Persist serializable args to sessionStorage too. The in-memory ref
      // survives the email-login flow; sessionStorage survives the Google
      // full-page redirect so we can auto-resume once the user lands back.
      try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify({
          path: window.location.pathname + window.location.search,
          deviceId: args.deviceId,
          itemType: args.itemType,
          itemId: args.itemId || null,
          amount: args.amount,
          currency: args.currency,
          userName: args.userName || '',
          userEmail: args.userEmail || '',
          savedAt: Date.now(),
        }));
      } catch (_) { /* ignore quota / private-mode errors */ }
      setAuthPromptOpen(true);
      trackEvent('purchase_gated', {
        item_type: args.itemType || 'premium',
        item_id: args.itemId || null,
      });
      return;
    }
    await _startCheckout(args);
  }, [user, isGuest, _startCheckout]);

  const dismissAuthPrompt = useCallback(() => {
    setAuthPromptOpen(false);
    pendingArgsRef.current = null;
    try { sessionStorage.removeItem(PENDING_KEY); } catch (_) { /* ignore */ }
  }, []);

  const handleAuthenticated = useCallback(async (newUser) => {
    setAuthPromptOpen(false);
    trackEvent('login_for_purchase_complete', { method: newUser?.picture ? 'google' : 'email' });
    const pending = pendingArgsRef.current;
    pendingArgsRef.current = null;
    try { sessionStorage.removeItem(PENDING_KEY); } catch (_) { /* ignore */ }
    if (pending) {
      // Enrich with the freshly-signed-in user's info so Razorpay's prefill is accurate.
      const enriched = {
        ...pending,
        userName: pending.userName || newUser?.name || '',
        userEmail: pending.userEmail || newUser?.email || '',
      };
      await _startCheckout(enriched);
    }
  }, [_startCheckout]);

  // Auto-resume after the Google full-page redirect. Once the user lands back
  // on the originating payment page (e.g. /premium) and is now authenticated,
  // we pull the persisted args out of sessionStorage and restart the Razorpay
  // modal. Only fires when the path matches so stale payloads don't trigger on
  // unrelated pages.
  useEffect(() => {
    const isUnauthed = !user || isGuest || user?.guest;
    if (isUnauthed) return;
    let raw;
    try { raw = sessionStorage.getItem(PENDING_KEY); } catch (_) { raw = null; }
    if (!raw) return;
    let saved;
    try { saved = JSON.parse(raw); } catch (_) {
      try { sessionStorage.removeItem(PENDING_KEY); } catch (_) { /* ignore */ }
      return;
    }
    if (!saved || (Date.now() - (saved.savedAt || 0) > PENDING_TTL_MS)) {
      try { sessionStorage.removeItem(PENDING_KEY); } catch (_) { /* ignore */ }
      return;
    }
    const currentPath = window.location.pathname + window.location.search;
    if (saved.path && saved.path !== currentPath) return; // don't resume on unrelated page
    try { sessionStorage.removeItem(PENDING_KEY); } catch (_) { /* ignore */ }
    trackEvent('purchase_auto_resume', {
      item_type: saved.itemType || 'premium',
      item_id: saved.itemId || null,
    });
    _startCheckout({
      deviceId: saved.deviceId || deviceId,
      itemType: saved.itemType,
      itemId: saved.itemId,
      amount: saved.amount,
      currency: saved.currency,
      userName: saved.userName || user?.name || '',
      userEmail: saved.userEmail || user?.email || '',
      // No per-page onSuccess callback survives the redirect — reload so the
      // page re-fetches entitlements and reveals the unlocked state.
      onSuccess: () => { try { window.location.reload(); } catch (_) { /* ignore */ } },
    });
    // We intentionally run this only once per (user, isGuest) transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isGuest]);

  const authDialog = (
    <LoginRequiredDialog
      open={authPromptOpen}
      onClose={dismissAuthPrompt}
      onAuthenticated={handleAuthenticated}
    />
  );

  return { pay, loading, error, authDialog };
}
